import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";
import { dealsApi } from "@/slices/deals/dealsApi";

/**
 * REP-199 Phase 3 — ProjectProperty terminal/primary mutations.
 *
 * Lives in its own slice (separate from dealsApi) because the routes
 * sit under `/api/project-properties/...` rather than `/api/deals/...`,
 * and the dealsApi slice's baseUrl is hard-set to `/api/deals`.
 *
 * Single-source-of-truth refactor: the swim-lane's in-progress stage
 * is driven entirely by workflow step state (`currentStageCategory`
 * on the parent deal's pursuits payload). `ProjectProperty.status` is
 * meaningful only for the `PASSED` terminal value — that signal can't
 * be expressed by step completion. Manual advance / drop-back
 * mutations were removed; brokers nudge the lane by completing items
 * in the workflow UI.
 *
 * Each mutation invalidates the parent deal's cache on `dealsApi` via
 * an `onQueryStarted` lifecycle hook — RTK Query tags are scoped per
 * api by default, so we dispatch a cross-api invalidate. We also
 * apply an optimistic patch to the dealsApi `getDealById` cache so
 * the row visuals (dim opacity + Passed pill, or the Active pin)
 * snap immediately rather than waiting for refetch.
 */

// The two known `include` strings used by callers of getDealById that
// render `pursuits[]` — DealLayout and DealOverview each request a
// different shape. We patch both. Any future caller with a new shape
// will just fall back to refetch-driven updates, no harm done.
const DEAL_INCLUDE_VARIANTS = [
  "propertyAssets,propertyAddresses,events,teams,sources,pursuits,activeDeal,parentDeal",
  "properties,workflow,sources,pursuits,parentDeal",
];

/**
 * Patch the cached deal payload for a given dealId by mutating each
 * pursuit's row that matches `projectPropertyId` (the id of the
 * ProjectProperty being transitioned). Returns the array of patchResult
 * handles so the caller can `undo()` them on mutation failure.
 *
 * `mutate(pursuit)` receives the draft pursuit and may mutate it.
 */
function patchPursuits(
  dispatch: any,
  dealId: string,
  projectPropertyId: string,
  mutate: (pursuit: any) => void,
) {
  return DEAL_INCLUDE_VARIANTS.map(include =>
    dispatch(
      dealsApi.util.updateQueryData(
        "getDealById",
        { id: dealId, include },
        (draft: any) => {
          const pursuits = draft?.data?.pursuits;
          if (!Array.isArray(pursuits)) return;
          for (const p of pursuits) {
            if (p?.projectPropertyId === projectPropertyId) {
              mutate(p);
            }
          }
        },
      ),
    ),
  );
}

/**
 * Patch the cached deal payload to set/clear the `activeDealPursuitId`
 * field. Used by `setPropertyPrimary` — when a user pins a pursuit as
 * active, the parent deal's primary-pursuit hint updates instantly so
 * RaceCanvas re-sorts the row to the top without waiting on refetch.
 */
function patchActivePursuit(
  dispatch: any,
  dealId: string,
  pursuitId: string | null,
) {
  return DEAL_INCLUDE_VARIANTS.map(include =>
    dispatch(
      dealsApi.util.updateQueryData(
        "getDealById",
        { id: dealId, include },
        (draft: any) => {
          if (!draft?.data) return;
          draft.data.activeDealPursuitId = pursuitId;
          draft.data.activeDealManuallyPinned = pursuitId != null;
        },
      ),
    ),
  );
}

export const projectPropertiesApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "projectPropertiesApi",
    baseUrl: "/api/project-properties",
  }),
  reducerPath: "projectPropertiesApi",
  tagTypes: ["ProjectProperty"],
  endpoints: (builder) => ({
    passProperty: builder.mutation<
      any,
      { id: string; dealId: string; reason?: string }
    >({
      query: ({ id, reason }) => ({
        url: `/${id}/pass`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      async onQueryStarted(
        { id, dealId },
        { dispatch, queryFulfilled },
      ) {
        const patches = patchPursuits(dispatch, dealId, id, p => {
          p.propertyStatus = "PASSED";
          // The pursuit's project status also flips to FAILED server-
          // side; mirror it locally so the filter toggle and the
          // dimmed-row visuals match without a refetch.
          p.status = "FAILED";
        });
        try {
          await queryFulfilled;
          dispatch(
            dealsApi.util.invalidateTags([
              { type: "Deals", id: dealId },
              { type: "Deals" },
            ])
          );
        } catch {
          patches.forEach(p => p.undo());
        }
      },
    }),
    setPropertyPrimary: builder.mutation<
      any,
      { id: string; dealId: string }
    >({
      query: ({ id }) => ({
        url: `/${id}/set-primary`,
        method: "POST",
      }),
      async onQueryStarted(
        { id, dealId },
        { dispatch, queryFulfilled, getState },
      ) {
        // Resolve the pursuit id from the cached deal. We need it to
        // update `activeDealPursuitId` (which is keyed by pursuit
        // project id, NOT ProjectProperty id). RTK Query's selector
        // types are scoped to each api's RootState — we're calling
        // dealsApi's selector from inside projectPropertiesApi's
        // lifecycle, so cast to `any` to bridge the two state slices
        // (the store has both registered; the selector reads its own
        // reducerPath out at runtime regardless of the static type).
        let pursuitId: string | null = null;
        const rootState = getState() as any;
        for (const include of DEAL_INCLUDE_VARIANTS) {
          const sel = dealsApi.endpoints.getDealById.select({
            id: dealId,
            include,
          });
          const cached = sel(rootState);
          const found = cached?.data?.data?.pursuits?.find(
            (p: any) => p.projectPropertyId === id,
          );
          if (found?.id) {
            pursuitId = found.id;
            break;
          }
        }
        const patches = pursuitId
          ? patchActivePursuit(dispatch, dealId, pursuitId)
          : [];
        try {
          await queryFulfilled;
          dispatch(
            dealsApi.util.invalidateTags([
              { type: "Deals", id: dealId },
              { type: "Deals" },
            ])
          );
        } catch {
          patches.forEach(p => p.undo());
        }
      },
    }),
  }),
});

export const {
  usePassPropertyMutation,
  useSetPropertyPrimaryMutation,
} = projectPropertiesApi;
